import React, { useEffect, useState } from 'react';

import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import type { LensCalendar, LensPeriod, LensResolvedPeriod } from '~/types/leLens';

import { announceLensFilter, describePeriodSelectionAnnouncement } from './lensAnnouncer';
import { LENS_DEFAULT_CALENDAR } from './lensCalendar';
import {
  COMPARE_TO_PRESETS,
  DATE_RANGE_PRESETS,
  matchingCompareTo,
  matchingDateRangePreset,
} from './lensPeriodPresets';

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

type PeriodRowsProps = {
  /** The window and comparison in force: the board's, with the viewer's own keys over it. */
  period: LensPeriod;
  boardPeriod: LensPeriod;
  resolved?: LensResolvedPeriod;
  calendar?: LensCalendar;
  appliesTo: Array<string>;
  isReloading?: boolean;
  canEdit: boolean;
  /** A partial period patches the viewer's override; null returns to the board's own. */
  onChange: (patch: Partial<LensPeriod> | null) => void;
  onSaveBoard: (period: LensPeriod) => Promise<void>;
};

export default function PeriodRows({
  period,
  boardPeriod,
  resolved,
  calendar = LENS_DEFAULT_CALENDAR,
  appliesTo,
  isReloading,
  canEdit,
  onChange,
  onSaveBoard,
}: PeriodRowsProps) {
  const fixed = typeof period.window === 'object' && 'from' in period.window ? period.window : undefined;
  const fixedFrom = fixed?.from;
  const fixedTo = fixed?.to;
  const [isCustomOpen, setIsCustomOpen] = useState(Boolean(fixed));
  const [draft, setDraft] = useState<{ from: string; to: string }>({ from: fixed?.from ?? '', to: fixed?.to ?? '' });
  const preset = matchingDateRangePreset(period);
  const compareTo = matchingCompareTo(period.comparison);
  const overridden = !same(period, boardPeriod);
  const backwards = Boolean(draft.from && draft.to && draft.from > draft.to);
  const selectedRange = isCustomOpen ? 'custom' : (preset?.id ?? 'current');

  useEffect(() => {
    setIsCustomOpen(Boolean(fixedFrom && fixedTo));
    if (fixedFrom && fixedTo) {
      setDraft({ from: fixedFrom, to: fixedTo });
    }
  }, [fixedFrom, fixedTo]);

  const applyWindow = (patch: Partial<LensPeriod>) => {
    onChange(patch);
    announceLensFilter(describePeriodSelectionAnnouncement({ ...period, ...patch }, appliesTo.length));
  };

  return (
    <Stack spacing={1.5} sx={{ width: '100%', minWidth: 0 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography variant="subtitle2">Date &amp; comparison</Typography>
        <Typography color="text.secondary" variant="caption">
          {calendar.zone}
        </Typography>
      </Stack>
      <FormControl fullWidth size="small">
        <InputLabel id="le-lens-date-range">Date range</InputLabel>
        <Select
          disabled={isReloading}
          label="Date range"
          labelId="le-lens-date-range"
          onChange={(event) => {
            if (event.target.value === 'custom') {
              setIsCustomOpen(true);
              return;
            }
            const chosen = DATE_RANGE_PRESETS.find((candidate) => candidate.id === event.target.value);
            if (chosen) {
              setIsCustomOpen(false);
              applyWindow({ anchor: chosen.anchor ?? 'yesterday', window: chosen.window });
            }
          }}
          value={selectedRange}
        >
          {selectedRange === 'current' && (
            <MenuItem disabled value="current">
              {resolved?.label.current ?? 'Current range'}
            </MenuItem>
          )}
          {DATE_RANGE_PRESETS.map((candidate) => (
            <MenuItem key={candidate.id} value={candidate.id}>
              {candidate.label}
            </MenuItem>
          ))}
          <MenuItem value="custom">Custom dates…</MenuItem>
        </Select>
        {!isCustomOpen && resolved && <FormHelperText sx={{ ml: 0 }}>{resolved.label.current}</FormHelperText>}
      </FormControl>
      {isCustomOpen && (
        <Stack spacing={1}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5 }}>
            <TextField
              label="From"
              onChange={(event) => setDraft((current) => ({ ...current, from: event.target.value }))}
              fullWidth
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
              type="date"
              value={draft.from}
            />
            <TextField
              error={backwards}
              helperText={backwards ? 'The end is before the start' : undefined}
              label="To"
              onChange={(event) => setDraft((current) => ({ ...current, to: event.target.value }))}
              fullWidth
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
              type="date"
              value={draft.to}
            />
          </Box>
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button
              disabled={!draft.from || !draft.to || backwards || isReloading}
              onClick={() => applyWindow({ anchor: 'yesterday', window: { from: draft.from, to: draft.to } })}
              size="small"
              variant="contained"
            >
              Apply dates
            </Button>
            {!fixed && (
              <Button onClick={() => setIsCustomOpen(false)} size="small">
                Cancel
              </Button>
            )}
          </Stack>
        </Stack>
      )}
      <FormControl fullWidth size="small">
        <InputLabel id="le-lens-compare-to">Compare to</InputLabel>
        <Select
          disabled={isReloading}
          label="Compare to"
          labelId="le-lens-compare-to"
          onChange={(event) => {
            const chosen = COMPARE_TO_PRESETS.find((candidate) => candidate.id === event.target.value);
            if (chosen) {
              onChange({ comparison: chosen.comparison });
            }
          }}
          value={compareTo?.id ?? 'custom'}
        >
          {!compareTo && (
            <MenuItem disabled value="custom">
              Custom dates
            </MenuItem>
          )}
          {COMPARE_TO_PRESETS.map((candidate) => (
            <MenuItem key={candidate.id} value={candidate.id}>
              {candidate.label}
            </MenuItem>
          ))}
        </Select>
        {resolved?.label.comparison && (resolved.label.comparison !== compareTo?.label || resolved.label.note) && (
          <FormHelperText sx={{ ml: 0 }}>
            {resolved.label.comparison}
            {resolved.label.note ? ` · ${resolved.label.note}` : ''}
          </FormHelperText>
        )}
      </FormControl>
      <Stack direction="row" spacing={1}>
        {overridden && (
          <Button onClick={() => onChange(null)} size="small" sx={{ color: 'text.secondary' }}>
            Back to board default
          </Button>
        )}
        {canEdit && overridden && (
          <Button onClick={() => onSaveBoard(period)} size="small" sx={{ color: 'text.secondary' }}>
            Set as board default
          </Button>
        )}
      </Stack>
    </Stack>
  );
}
